<template>
    <div>
        <v-card class="bank-statements" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('bank_statements.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="openForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <bank-statements-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                >
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.date | date }}</td>
                            <td>{{ item.document_number }}</td>
                            <td>{{ item.bank_account.bank.name }}</td>
                            <td>{{ item.bank_account.number }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>

                                        <v-list-item
                                            key="view"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-eye</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.view') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="!!item.recorded_at"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon
                                                    :disabled="!!item.recorded_at"
                                                    :class="{'error--text': !item.recorded_at}"
                                                >
                                                    fal fa-times
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{'error--text': !item.recorded_at}">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import BankStatementsSearch from '@/views/shared/admin/bankStatements/BankStatementsSearch';

export default {
    components: {
        BankStatementsSearch,
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$t('bank_statements.date'), value: 'date' },
                { text: this.$t('bank_statements.document_number'), value: 'document_number' },
                { text: this.$t('bank_statements.bank'), value: 'bank_name' },
                { text: this.$t('bank_statements.bank_account'), value: 'bank_account_number' },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/bank-statements?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data;
                this.itemsTotal = response.data.total;
            });
        },

        openForm(item = null) {
            if(!item) {
                this.$router.push({ name: 'adminBankStatementCreate' }).catch(err => {});
                return;
            }

            this.$router.push({ name: 'adminBankStatementUpdate', params: { id: item.id } }).catch(err => {});
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('bank_statements.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/bank-statements/${item.id}`, {}).then(response => {
                        this.items = this.items.filter(i => i.id !== item.id);
                        this.$snackbar(this.$t('bank_statements.deleted'), 'success');
                        this.itemsTotal--;
                    });
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.bank-statements {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
